import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const to = loc.state?.from || "/dashboard";
      nav(to, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-app">
      {/* Left: brand panel */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1543134968-8752069dbf58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx0aGFpJTIwc3BhJTIwbWFzc2FnZSUyMHdlbGxuZXNzfGVufDB8fHx8MTc4NTU4Mjg4NXww&ixlib=rb-4.1.0&q=85"
          alt="Aromathai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-brand/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/15 grid place-items-center backdrop-blur">
              <Leaf className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-medium tracking-tight">Aromathai</div>
              <div className="text-[10px] uppercase font-bold tracking-[0.25em] text-white/70">
                MIS Dashboard
              </div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter leading-[1.05]">
              A quiet<br />command centre<br />for every branch.
            </h1>
            <p className="mt-6 max-w-md text-white/80 text-sm leading-relaxed">
              Upload a monthly MIS pack. Watch it become insight — revenue trends,
              retention, therapist utilization, lost business — across every outlet.
            </p>
          </div>
          <div className="text-[10px] font-mono text-white/50 uppercase tracking-[0.2em]">
            v1 · Internal · Access by invitation
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-brand text-white grid place-items-center">
              <Leaf className="w-4 h-4" />
            </div>
            <div className="font-heading font-medium">Aromathai MIS</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
            Sign in
          </div>
          <h2 className="font-heading text-3xl font-light tracking-tight text-stone-900 mb-8">
            Welcome back.
          </h2>

          {err && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1 h-11 bg-white border-stone-300 focus-visible:ring-brand"
                placeholder="you@aromathai.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1 h-11 bg-white border-stone-300 focus-visible:ring-brand"
                placeholder="••••••••••••"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full mt-8 h-11 bg-brand hover:bg-brand-hover text-white font-medium"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="mt-8 text-[11px] text-stone-500 font-mono leading-relaxed">
            No public signup. Access is provisioned by the account owner.
            Locked after 5 failed attempts within 15 minutes.
          </div>
        </form>
      </div>
    </div>
  );
}
